import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { subscribeAdvisersData } from "../../../context/Adviser/Utils/ExportAdvisersData";
import { updateAdviserRegulatoryText } from "../../../context/Adviser/Utils/UpdateRegulatoryText";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import AlertBanner from "../../DesignSystem/AlertBanner/AlertBanner";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { logAuditChanges } from "../AuditLogs/auditLogger";
import {
  NEW_COMPANY_NAME,
  OLD_COMPANY_NAME,
  getRegulatoryWordingStatus,
  replaceRegulatoryCompanyName,
} from "../Utils/regulatoryWordingReplacement";
import "../../../css/Adviser/RegulatoryWordingUpdates.css";

const adviserName = (adviser) =>
  [adviser.preferredName || adviser.firstName, adviser.lastName]
    .filter(Boolean)
    .join(" ") || adviser.email;

const STATUS_LABELS = {
  "needs-update": "Needs update",
  updated: "Already correct",
  "not-applicable": "Phrase not found",
  missing: "No wording",
};

const HighlightedWording = ({ text, phrase, variant }) => {
  const parts = String(text || "").split(phrase);

  return parts.map((part, index) => (
    <span key={`${variant}-${index}`}>
      {part}
      {index < parts.length - 1 ? (
        <mark className={`regWordingHighlight regWordingHighlight--${variant}`}>
          {phrase}
        </mark>
      ) : null}
    </span>
  ));
};

export default function RegulatoryWordingUpdatesPage() {
  const { userPermissions } = useUserPermissions();
  const [advisers, setAdvisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState("");
  const [updatingAll, setUpdatingAll] = useState(false);
  const [updateProgress, setUpdateProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canEdit =
    userPermissions.includes("Compliance Support") ||
    userPermissions.includes("Administrator");

  useEffect(() =>
    subscribeAdvisersData(
      (nextAdvisers) => {
        setAdvisers(nextAdvisers);
        setLoading(false);
      },
      () => {
        setError("Could not load adviser regulatory wording. Please try again.");
        setLoading(false);
      },
    ), []);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return advisers
      .map((adviser) => ({
        ...adviser,
        wordingStatus: getRegulatoryWordingStatus(adviser.regulatoryText),
        proposedText: replaceRegulatoryCompanyName(adviser.regulatoryText),
      }))
      .filter((adviser) => {
        if (!term) return true;
        return `${adviserName(adviser)} ${adviser.email} ${adviser.regulatoryText}`
          .toLowerCase()
          .includes(term);
      })
      .sort((a, b) => {
        if (a.wordingStatus === "needs-update" && b.wordingStatus !== "needs-update") return -1;
        if (a.wordingStatus !== "needs-update" && b.wordingStatus === "needs-update") return 1;
        return adviserName(a).localeCompare(adviserName(b));
      });
  }, [advisers, search]);

  const needsUpdateCount = advisers.filter(
    (adviser) => getRegulatoryWordingStatus(adviser.regulatoryText) === "needs-update",
  ).length;

  const writeAdviserUpdate = async (adviser) => {
    const oldValue = String(adviser.regulatoryText || "");
    const newValue = replaceRegulatoryCompanyName(oldValue);
    if (oldValue === newValue) return false;

    await updateAdviserRegulatoryText(adviser.id, newValue);
    setAdvisers((current) =>
      current.map((item) =>
        item.id === adviser.id ? { ...item, regulatoryText: newValue } : item,
      ),
    );

    logAuditChanges({
      entity: "Adviser",
      entityId: adviser.id,
      changes: [{
        section: "adviserDoc",
        field: "adviserDoc.regulatoryText",
        oldValue,
        newValue,
      }],
      extra: { source: "Regulatory wording update page" },
    }).catch((reason) => console.error("Regulatory wording audit log failed:", reason));

    return true;
  };

  const updateOneAdviser = async (adviser) => {
    if (adviser.wordingStatus !== "needs-update" || savingId || updatingAll) return;

    setSavingId(adviser.id);
    setError("");
    setSuccess("");
    try {
      await writeAdviserUpdate(adviser);
      setSuccess(`${adviserName(adviser)}'s regulatory wording was updated.`);
    } catch (reason) {
      console.error("Regulatory wording update failed:", reason);
      setError(`Could not update ${adviserName(adviser)}. Please try again.`);
    } finally {
      setSavingId("");
    }
  };

  const updateAllAdvisers = async () => {
    const affected = advisers.filter(
      (adviser) => getRegulatoryWordingStatus(adviser.regulatoryText) === "needs-update",
    );
    if (!affected.length || savingId || updatingAll) return;

    const confirmed = window.confirm(
      `Update the regulatory wording for all ${affected.length} affected adviser${affected.length === 1 ? "" : "s"}?`,
    );
    if (!confirmed) return;

    setUpdatingAll(true);
    setUpdateProgress({ completed: 0, total: affected.length });
    setError("");
    setSuccess("");

    let updated = 0;
    const failed = [];
    for (const adviser of affected) {
      try {
        if (await writeAdviserUpdate(adviser)) updated += 1;
      } catch (reason) {
        console.error(`Regulatory wording update failed for ${adviser.id}:`, reason);
        failed.push(adviserName(adviser));
      }
      setUpdateProgress((current) => ({ ...current, completed: current.completed + 1 }));
    }

    if (updated) {
      setSuccess(`${updated} adviser${updated === 1 ? "" : "s"} updated successfully.`);
    }
    if (failed.length) {
      setError(
        `${failed.length} adviser${failed.length === 1 ? "" : "s"} could not be updated: ${failed.join(", ")}.`,
      );
    }
    setUpdatingAll(false);
  };

  return (
    <div className="main-content">
      <Helmet><title>Regulatory Wording Updates</title></Helmet>
      <Sidebar />
      <div className="Main-Container">
        <div className="regWordingPage">
          <Breadcrumb items={[
            { label: "All Staff", to: "/Department/AllStaff" },
            { label: "Advisers", to: "/Advisers" },
            { label: "Regulatory Wording Updates" },
          ]} />

          <div className="regWordingHeader">
            <div>
              <h2>Regulatory Wording Updates</h2>
              <p className="Content-Small muted">
                Review and update each adviser separately. This page replaces only the exact company name shown below.
              </p>
            </div>
            <div className="regWordingHeaderActions">
              <div className="regWordingCount">
                <strong>{loading ? "–" : needsUpdateCount}</strong>
                <span>still need updating</span>
              </div>
              {canEdit ? (
                <PrimaryButton
                  type="button"
                  label={
                    updatingAll
                      ? `Updating ${updateProgress.completed} of ${updateProgress.total}…`
                      : `Update all${needsUpdateCount ? ` (${needsUpdateCount})` : ""}`
                  }
                  disabled={loading || !needsUpdateCount || Boolean(savingId) || updatingAll}
                  onClick={updateAllAdvisers}
                />
              ) : null}
            </div>
          </div>

          {error ? <AlertBanner type="error" message={error} onClose={() => setError("")} /> : null}
          {success ? <AlertBanner type="success" message={success} onClose={() => setSuccess("")} /> : null}

          {!canEdit ? (
            <AlertBanner type="error" showClose={false} message="You need Compliance Support or Administrator permission to use this page." />
          ) : (
            <>
              <div className="regWordingReplacement">
                <div><span>Current company name</span><strong>{OLD_COMPANY_NAME}</strong></div>
                <div aria-hidden="true" className="regWordingArrow">→</div>
                <div><span>Replacement company name</span><strong>{NEW_COMPANY_NAME}</strong></div>
              </div>

              <label className="regWordingSearchLabel" htmlFor="reg-wording-search">Search advisers</label>
              <input
                id="reg-wording-search"
                className="regWordingSearch"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by adviser name, email or wording"
              />

              {loading ? <div className="regWordingEmpty">Loading adviser wording…</div> : null}
              {!loading && rows.length === 0 ? <div className="regWordingEmpty">No advisers match your search.</div> : null}

              <div className="regWordingList">
                {rows.map((adviser) => {
                  const needsUpdate = adviser.wordingStatus === "needs-update";
                  return (
                    <article className={`regWordingCard ${needsUpdate ? "regWordingCard--needsUpdate" : ""}`} key={adviser.id}>
                      <div className="regWordingCardHeader">
                        <div><h3>{adviserName(adviser)}</h3><span>{adviser.email}</span></div>
                        <span className={`regWordingStatus regWordingStatus--${adviser.wordingStatus}`}>
                          {STATUS_LABELS[adviser.wordingStatus]}
                        </span>
                      </div>

                      <div className="regWordingComparison">
                        <div>
                          <span className="regWordingLabel">Current wording</span>
                          <p>
                            {adviser.regulatoryText ? (
                              <HighlightedWording
                                text={adviser.regulatoryText}
                                phrase={OLD_COMPANY_NAME}
                                variant="old"
                              />
                            ) : "No regulatory wording saved."}
                          </p>
                        </div>
                        {needsUpdate ? (
                          <div>
                            <span className="regWordingLabel">Wording after update</span>
                            <p>
                              <HighlightedWording
                                text={adviser.proposedText}
                                phrase={NEW_COMPANY_NAME}
                                variant="new"
                              />
                            </p>
                          </div>
                        ) : null}
                      </div>

                      {needsUpdate ? (
                        <div className="regWordingActions">
                          <PrimaryButton
                            type="button"
                            label={savingId === adviser.id ? "Updating…" : "Update this adviser"}
                            disabled={Boolean(savingId) || updatingAll}
                            onClick={() => updateOneAdviser(adviser)}
                          />
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
